import React, { createContext, useContext } from 'react';
import { useAppContainerContext } from "./appContainer/context";
import { Overlay, Popover } from 'react-bootstrap';
import './OpenChatButton.css';



export const SidebarContext = createContext();
export const useSidebar = () => useContext(SidebarContext);

export function OpenChatButton({textSourceRef, fieldName, onOpenChat, onChange}) {
  const { setIsSidebarOpen, setSelectedTextForSidebar, setSelectedFieldName} = useAppContainerContext();
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [hasSelection, setHasSelection] = React.useState(false);
  const buttonRef = React.useRef();
  const hoverTimeoutRef = React.useRef(null);
  const lastSelectionRef = React.useRef(null); // Track last selection

  
  const handleOpenChat = () => {
    const input = textSourceRef?.current;
    if(input == undefined) return;   
    let selectedText = getSelectedText(input);
    
    setSelectedTextForSidebar(selectedText);
    setSelectedFieldName(fieldName);
  
    if (onOpenChat) {
      onOpenChat(selectedText);
    } else {
      setIsSidebarOpen(true);
    }
  };

  React.useEffect(() => {
    const input = textSourceRef?.current;
    if (!input) return;

    const checkSelection = () => {
      try {
        if (isQuillRef(input)) {
          const editor = input.getEditor?.();
          const range = editor?.getSelection();
          const hasSelection = !!(range && range.length > 0);
          if(!hasSelection) {
            setHasSelection(false);
            return;
          };
          // Only update if there's a new selection (different from current)
          const currentSelection = editor.getSelection();
          const hasNewSelection = hasSelection && 
            (currentSelection.index !== lastSelectionRef.current?.index || 
             currentSelection.length !== lastSelectionRef.current?.length);
               
          setHasSelection(hasNewSelection);
        } else {
          const selLen = Math.abs((input.selectionEnd || 0) - (input.selectionStart || 0));
          const hasSelection = selLen > 0;
          setHasSelection(hasSelection);
          
          // For regular inputs, we can't easily apply background color
          // The selection will be highlighted by the browser's default selection
        }
      } catch (e) {
        setHasSelection(false);
      }
    };

    // Attach listeners only to the textSourceRef element
    if (isQuillRef(input)) {
      const editor = input.getEditor?.();
      // Only listen to selection changes within the Quill editor
      editor?.on('selection-change', (range, oldRange, source) => {
        // Only trigger if the change is from user interaction
        if (source === 'user') {
          checkSelection();
        }
      });
      // Initial check
      checkSelection();
      return () => {
        editor?.off('selection-change');
      };
    } else {
      // For regular inputs, only listen to events on the input itself
      const handleInputEvent = (e) => {
        // Only trigger if the event target is the input itself
        if (e.target === input) {
          checkSelection();
        }
      };
      
      input.addEventListener('mouseup', handleInputEvent);
      input.addEventListener('keyup', handleInputEvent);
      // Initial check
      checkSelection();
      return () => {
        input.removeEventListener('mouseup', handleInputEvent);
        input.removeEventListener('keyup', handleInputEvent);
      };
    }
  }, [textSourceRef]);

  const getSelectedText = (input) => {
    if (!input) return '';
  
    try {
      // Handle ReactQuill instance
      if (isQuillRef(input)) {
        const editor = input.getEditor?.();
        const range = editor?.getSelection();
  
      
        if (range?.length > 0) {
          // Remove all text marked with #facc15 (yellow) first
          const length = editor.getLength();
          const contents = editor.getContents();
          
          // Find all ranges with yellow background class and remove the background class
          let currentIndex = 0;
          contents.ops.forEach(op => {
            if (op.attributes && op.attributes.bg === 'bg-yellow') {
              editor.formatText(currentIndex, op.insert.length, { bg: null });
            }
            currentIndex += op.insert.length;
          });
          
          console.log("new yellow " + range.index + " " + range.length);
          // Apply yellow background class to new selected text
          editor.formatText(range.index, range.length, { bg: 'bg-yellow' }, 'user'); // yellow background class
          
          // Store the new selection
          lastSelectionRef.current = { index: range.index, length: range.length };
          
          // Trigger onChange to notify parent component of the formatting change
          //onChange?.(editor.getContents(), editor.getContents(), 'user', editor);
      
          return editor.getText(range.index, range.length);
        }
  
        return '';
      }
  
      // Handle <textarea> or <input>
      return input.value?.substring(input.selectionStart, input.selectionEnd) || '';
    } catch (error) {
      console.error('Error getting selected text:', error);
      return '';
    }
  };
  

  const isQuillRef = (input) => typeof input.getEditor === 'function';

  return (
    <>
      <button 
        ref={buttonRef}
        onClickCapture={handleOpenChat}
        className={`refine-button ${hasSelection ? 'refine-button--active' : ''}`}
        onClick={handleOpenChat}
        onMouseEnter={() => {
          // Clear any existing timeout
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
          }
          // Set timeout to show tooltip after 2 seconds
          hoverTimeoutRef.current = setTimeout(() => {
            setShowTooltip(true);
          }, 1000);
        }}
        onMouseLeave={() => {
          // Clear timeout and hide tooltip immediately
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
          }
          setShowTooltip(false);
        }}
      >
        <span className="refine-button-icon">🤖</span> Refine Highlighted Section Only
      </button>
      
      <Overlay
        target={buttonRef.current}
        show={showTooltip}
        placement="top"
        onHide={() => setShowTooltip(false)}
      >
        <Popover className="bg-dark text-white">
          <Popover.Body className="text-white">
            Highlight the text you want to refine, I am trained to help improve grammar, clarity and integrate your key wordsphrases to your narrative
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  );
} 